import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentUser, signIn, signOut, signUp, AuthResponse } from '@/services/auth';
import { AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { appStorage } from '@/lib/appStorage';
import { clearAllUserData } from '@/services/cleanupService';

export interface UserData {
  id: string;
  nome: string;
  email: string;
  foto_perfil?: string;
  tipo_de_conta: 'free' | 'premium';
  created_at?: string;
  updated_at?: string;
  data_fim_periodo?: string;
}

export interface AuthContextType {
  user: UserData | null;
  loading: boolean;
  error: AuthError | null;
  login: (email: string, password: string) => Promise<{ user: UserData | null; error: AuthError | null }>;
  signup: (email: string, password: string, nome: string) => Promise<{ user: UserData | null; error: AuthError | null }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (user: UserData | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Simplificado: Mensagens de erro mais amigáveis
const getErrorMessage = (error: any): AuthError => {
  const message = error instanceof Error ? error.message : String(error);
  const friendlyMessages: Record<string, string> = {
    'Invalid login credentials': 'Email ou senha incorretos',
    'Email not confirmed': 'Por favor, confirme seu email antes de fazer login',
    'User already registered': 'Este email já está cadastrado',
    'Invalid email': 'Email inválido',
    'Password is too short': 'A senha deve ter pelo menos 6 caracteres'
  };
  return new AuthError(friendlyMessages[message] || message);
};

// Função auxiliar para recuperar usuário do storage
const getUserFromStorage = (): UserData | null => {
  try {
    // Apenas tenta localStorage direto para evitar recuperação após logout
    const cached = localStorage.getItem('user');
    if (cached) {
      const parsedUser = JSON.parse(cached);
      if (parsedUser && parsedUser.id) {
        return parsedUser;
      }
    }
    return null;
  } catch {
    return null;
  }
};
  
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserData | null>(() => getUserFromStorage());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const checkingUser = useRef(false);

  // Monitor de conexão
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Simplificado: Função updateUser mais direta
  const updateUser = useCallback((newUser: UserData | null) => {
    setUser(newUser);
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('user');
    }
  }, []);

  // Simplificado: checkUser mais eficiente
  const checkUser = useCallback(async () => {
    if (checkingUser.current) return;
    
    try {
      checkingUser.current = true;

      if (!isOnline) {
        const storedUser = getUserFromStorage();
        updateUser(storedUser);
        return;
      }

      const { user: currentUser } = await getCurrentUser();
      if (JSON.stringify(currentUser) !== JSON.stringify(user)) {
        updateUser(currentUser);
      }
    } catch {
      updateUser(null);
    } finally {
      checkingUser.current = false;
      setLoading(false);
    }
  }, [updateUser, isOnline, user]);

  // Simplificado: refreshUser mais direto
  const refreshUser = useCallback(async () => {
    if (!isOnline || !user?.id || checkingUser.current) return;
    
    try {
      checkingUser.current = true;
      const { user: refreshedUser } = await getCurrentUser();
      if (refreshedUser && JSON.stringify(refreshedUser) !== JSON.stringify(user)) {
        updateUser(refreshedUser);
      }
    } catch (error) {
      console.warn('Erro ao atualizar usuário:', error);
    } finally {
      checkingUser.current = false;
    }
  }, [user?.id, updateUser, isOnline, user]);

  // Gerenciamento centralizado de sessão
  useEffect(() => {
    // Inicialização
    checkUser();

    if (!isOnline) return;

    // Listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Salva dados da sessão
        await appStorage.setItem('user_session', JSON.stringify({
          id: session.user.id,
          email: session.user.email,
          expires_at: session.expires_at
        }));

        // Agenda renovação do token
        if (session.expires_at) {
          const refreshTime = (session.expires_at * 1000) - Date.now() - (5 * 60 * 1000); // 5 min antes
          setTimeout(() => {
            if (navigator.onLine) {
              supabase.auth.refreshSession();
            }
          }, Math.max(refreshTime, 60000));
        }

        checkUser();
      } else if (event === 'SIGNED_OUT') {
        await appStorage.removeItem('user_session');
        updateUser(null);
      } else if (event === 'TOKEN_REFRESHED') {
        checkUser();
      }
    });

    // Listener para atualizações externas do usuário (ex: RevenueCat)
    const handleUserUpdate = (event: CustomEvent<UserData>) => {
      updateUser(event.detail);
    };
    window.addEventListener('user-updated', handleUserUpdate as EventListener);

    // Verificação periódica de sessão (30 minutos)
    const sessionCheck = setInterval(async () => {
      if (!document.hidden && user && navigator.onLine && !checkingUser.current) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            const storedUser = getUserFromStorage();
            if (!storedUser) {
              updateUser(null);
            }
          } else if (session.expires_at && (session.expires_at * 1000) - Date.now() < 30 * 60 * 1000) {
            await supabase.auth.refreshSession();
          }
        } catch (error) {
          console.warn('Erro na verificação de sessão:', error);
        }
      }
    }, 30 * 60 * 1000); // Aumentado para 30 minutos

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('user-updated', handleUserUpdate as EventListener);
      clearInterval(sessionCheck);
    };
  }, [user, checkUser, updateUser, isOnline]);

  const login = async (email: string, password: string) => {
    if (loading) {
      return { user: null, error: getErrorMessage('Login já em andamento') };
    }
    
    if (!isOnline) {
      return { user: null, error: getErrorMessage('Sem conexão com a internet') };
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await signIn({ email, password });
      
      if (result.error) {
        const authError = getErrorMessage(result.error);
        setError(authError);
        return { user: null, error: authError };
      }
      
      if (result.user) {
        updateUser(result.user);
        return { user: result.user, error: null };
      } else {
        const error = getErrorMessage('Usuário não encontrado após autenticação');
        setError(error);
        return { user: null, error };
      }
    } catch (error) {
      const authError = getErrorMessage(error);
      setError(authError);
      return { user: null, error: authError };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string, nome: string) => {
    if (loading) return { user: null, error: getErrorMessage('Cadastro já em andamento') };
    if (!isOnline) return { user: null, error: getErrorMessage('Sem conexão com a internet') };

    setLoading(true);
    setError(null);

    try {
      const result = await signUp(email, password, nome);
      return result;
    } catch (error) {
      const authError = getErrorMessage(error);
      setError(authError);
      return { user: null, error: authError };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Primeiro força o signOut do Supabase
      await signOut();
      
      // Limpa TUDO do storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Força o estado para null
      setUser(null);
      
      // Força uma nova verificação de usuário que deve retornar null
      setTimeout(() => {
        setUser(null);
      }, 100);
      
    } catch (error) {
      // Mesmo com erro, força a limpeza
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
    user,
    loading,
    error,
    login,
    signup,
    logout,
    refreshUser,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
